import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import Cookies from "js-cookie";
import { useSearchParams } from "react-router-dom";

import en from "../assets/locales/en.json";
import cn from "../assets/locales/cn.json";

const translations = { en, cn };

export const availableLangs = ["en", "cn"];
const defaultLang = "en";

const LanguageContext = createContext();

const getInitialLanguage = (urlLang) => {
  // 1. Highest Priority: URL query parameter ('?lang=...')
  if (urlLang && availableLangs.includes(urlLang)) {
    // If we get a valid language from the URL, set it as the new cookie.
    Cookies.set("langId", urlLang, { expires: 365 });
    return urlLang;
  }

  // 2. Second Priority: Existing cookie
  const cookieLang = Cookies.get("langId");
  if (cookieLang && availableLangs.includes(cookieLang)) {
    return cookieLang;
  }

  // 3. Third Priority: Browser's language setting
  const browserLang = navigator.language.split("-")[0];
  if (availableLangs.includes(browserLang)) {
    return browserLang;
  }

  // 4. Fallback: Default language
  return defaultLang;
};

export function LanguageProvider({ children }) {
  const [searchParams] = useSearchParams();
  const [langId, setLangId] = useState(() =>
    getInitialLanguage(searchParams.get("lang"))
  );

  const switchLanguage = useCallback((newLangId) => {
    if (availableLangs.includes(newLangId)) {
      setLangId(newLangId);
      Cookies.set("langId", newLangId, { expires: 365 });
    }
  }, []);

  // CHANGE 2: The cycle logic now lives inside the provider.
  // We use `useCallback` for performance, ensuring the function isn't
  // recreated on every render unless its dependencies (langId) change.
  const cycleLanguage = useCallback(() => {
    const currentIndex = availableLangs.indexOf(langId);
    const nextIndex = (currentIndex + 1) % availableLangs.length;
    const nextLang = availableLangs[nextIndex];
    switchLanguage(nextLang);
  }, [langId, switchLanguage]);

  const getTranslation = useMemo(() => {
    return (key) => {
      return (
        translations[langId]?.[key] || translations[defaultLang][key] || key
      );
    };
  }, [langId]);

  const value = {
    langId,
    switchLanguage,
    getTranslation: getTranslation,
    cycleLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
